import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Check, Plus, Search, SearchX } from "lucide-react";

import { Drawer } from "@/components/ui/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import {
  BASE_COLUMNS,
  SIZE_LABELS,
  SIZE_SPANS,
  TALLEST_ROWS,
  TONE_ACCENT_FG_VAR,
  TONE_ACCENT_VAR,
} from "../grid.constants";
import { allowedSizes } from "../grid.utils";
import type { IWidgetDefinition, IWidgetPlacement } from "../types";
import { WidgetErrorBoundary } from "./WidgetErrorBoundary";

interface WidgetLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  /** Already filtered by permission — the library never lists what the user cannot see. */
  widgets: IWidgetDefinition[];
  placements: IWidgetPlacement[];
  onAdd: (type: string) => void;
}

const ALL_CATEGORIES = "All";

/** Ties the category filters to the list they narrow, via `aria-controls`. */
const RESULTS_ID = "widget-library-results";

/**
 * The widget catalogue.
 *
 * Stays open after an add so building a canvas is one pass rather than a
 * drawer-open-per-widget loop, and the canvas behind it flashes a ring on the
 * widget that just landed so you can see the result without closing anything.
 *
 * Every row renders the real widget at its smallest size against the caller's
 * real data, so the choice is made by looking rather than by reading a name and
 * finding out afterwards. That is affordable only because the compact layouts
 * were built to skip their secondary queries — a `sm` preview costs at most the
 * one request the widget would make on the canvas anyway, and shares its cache
 * entry with the copy already there.
 *
 * A widget already on the canvas is shown as added and disabled rather than
 * hidden: a row that vanishes reads as a bug, a disabled one explains itself.
 */
export const WidgetLibrary = ({
  isOpen,
  onClose,
  widgets,
  placements,
  onAdd,
}: WidgetLibraryProps) => {
  const { t } = useTranslation(["common"]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);

  const placedTypes = useMemo(
    () => new Set(placements.map((placement) => placement.type)),
    [placements],
  );

  const categories = useMemo(
    () => [
      ALL_CATEGORIES,
      ...new Set(widgets.map((widget) => widget.category)),
    ],
    [widgets],
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return widgets.filter((widget) => {
      const inCategory =
        category === ALL_CATEGORIES || widget.category === category;
      if (!inCategory) return false;
      if (!needle) return true;
      // `name`/`description`/`category` hold i18n keys, so search has to run
      // against the RESOLVED text — matching the key would never hit in Arabic.
      return (
        t(widget.name).toLowerCase().includes(needle) ||
        t(widget.description).toLowerCase().includes(needle) ||
        t(widget.category).toLowerCase().includes(needle)
      );
    });
  }, [widgets, query, category, t]);

  const remaining = widgets.filter(
    (widget) => !(widget.singleton && placedTypes.has(widget.type)),
  ).length;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={t("common:dashboard_grid.widget_library")}
      size="md"
      styles={{ body: "flex min-h-0 flex-1 flex-col overflow-hidden p-0" }}
    >
      <div className="flex shrink-0 flex-col gap-2.5 border-b border-border px-4 pt-1 pb-3">
        <div className="relative">
          <Search
            size={14}
            aria-hidden
            className="pointer-events-none absolute top-1/2 start-2.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("common:dashboard_grid.search_widgets")}
            aria-label={t("common:dashboard_grid.search_widgets")}
            className="h-9 ps-8"
          />
        </div>

        {/* Categories as filter chips, not headings: with ten widgets the list is
            short enough that filtering beats scrolling to a section.

            `aria-pressed` toggles in a labelled group, not `role="tab"` in a
            `role="tablist"`. There is no tabpanel here — these filter one list
            that is always on screen — and, more to the point, a tablist is a
            promise about the keyboard: one tab stop for the set, arrow keys to
            move between them. This set kept none of that. Claiming the role
            without the behaviour is worse than not claiming it, because a
            screen-reader user is told to press arrow keys that do nothing.
            Plain toggles describe exactly what these are and what they do.

            `aria-controls` names the list they filter, so the relationship the
            tablist was standing in for survives the change. */}
        <div
          role="group"
          aria-label={t("common:dashboard_grid.filter_widgets_by_category")}
          className="flex flex-wrap gap-1"
        >
          {categories.map((entry) => {
            const isActive = entry === category;
            return (
              <button
                key={entry}
                type="button"
                aria-pressed={isActive}
                aria-controls={RESULTS_ID}
                onClick={() => setCategory(entry)}
                // `h-7` and `text-xs`, matching the Add button in every row —
                // the drawer's other rounded control. These were 11px in a 26px
                // box next to that button's 12px in a 28px box: the same shape,
                // a size apart, which reads as one of them being off rather
                // than as a hierarchy. 11px also made the drawer's primary
                // filter the smallest text in it, directly beneath a 14px
                // search field. `font-medium` for the same reason — the button
                // system's weight, rather than a semibold that only this
                // control used.
                className={cn(
                  "widget-focus inline-flex h-7 items-center rounded-full border px-2.5 text-xs font-medium transition-colors",
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                )}
              >
                {entry === ALL_CATEGORIES
                  ? t("common:dashboard_grid.all_categories")
                  : t(entry)}
              </button>
            );
          })}
        </div>
      </div>

      <div
        id={RESULTS_ID}
        className="min-h-0 flex-1 overflow-y-auto scrollbar-thin px-4 py-3"
      >
        {matches.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <span className="grid size-9 place-items-center rounded-full bg-muted text-muted-foreground">
              <SearchX size={16} aria-hidden />
            </span>
            <p className="text-sm text-muted-foreground">
              {t("common:dashboard_grid.nothing_matches_search")}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {matches.map((widget) => {
              const isPlaced =
                Boolean(widget.singleton) && placedTypes.has(widget.type);
              const Icon = widget.icon;
              const PreviewBody = widget.component;

              return (
                <li key={widget.type}>
                  <div
                    className={cn(
                      "flex items-start gap-3 rounded-md border border-border bg-surface p-3 transition-colors",
                      isPlaced ? "opacity-70" : "hover:border-primary/40",
                    )}
                  >
                    <span
                      style={{
                        ["--widget-accent" as string]:
                          TONE_ACCENT_VAR[widget.tone],
                        ["--widget-accent-fg" as string]:
                          TONE_ACCENT_FG_VAR[widget.tone],
                      }}
                      className="widget-app-chip grid size-9 shrink-0 place-items-center"
                    >
                      <Icon size={16} aria-hidden />
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {t(widget.name)}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {t(widget.description)}
                      </p>

                      {/* The widget itself, live. `pointer-events-none` and
                          `inert` together stop it being a second, confusing set
                          of links and buttons inside a list row whose only
                          action is Add. */}
                      <div
                        inert
                        aria-hidden
                        className="widget-library-preview mt-2 h-[74px] overflow-hidden rounded-sm border border-border bg-surface p-2"
                      >
                        <WidgetErrorBoundary widgetName={t(widget.name)}>
                          {/* `isEditing` doubles as "inert" for a widget body:
                              it is the flag every widget already uses to drop
                              its own links. */}
                          <PreviewBody
                            size={allowedSizes(widget)[0]}
                            isEditing
                          />
                        </WidgetErrorBoundary>
                      </div>

                      {/* Footprint previews — the sizes this widget can take, drawn
                          to scale rather than named. */}
                      <div className="mt-2 flex items-center gap-1.5">
                        {allowedSizes(widget).map((size) => {
                          const span = SIZE_SPANS[size];
                          return (
                            <span
                              key={size}
                              title={SIZE_LABELS[size]}
                              className="flex h-3.5 w-5 items-start rounded-[2px] border border-border p-[1.5px]"
                            >
                              <span
                                className="rounded-[1px] bg-muted-foreground/50"
                                style={{
                                  width: `${(span.w / BASE_COLUMNS) * 100}%`,
                                  height: `${(span.h / TALLEST_ROWS) * 100}%`,
                                }}
                              />
                            </span>
                          );
                        })}
                        {/* `text-widget-label`, the same 11px the drawer's
                            footer uses. At 10px this was the only text of its
                            size anywhere in the library. */}
                        <span className="ms-0.5 text-widget-label font-medium text-muted-foreground">
                          {t("common:dashboard_grid.size_count", {
                            count: allowedSizes(widget).length,
                          })}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant={isPlaced ? "ghost" : "outline-primary"}
                      size="xs"
                      disabled={isPlaced}
                      onClick={() => onAdd(widget.type)}
                      aria-label={t(
                        "common:dashboard_grid.add_widget_to_dashboard",
                        {
                          widget: t(widget.name),
                        },
                      )}
                      className="shrink-0"
                    >
                      {isPlaced ? (
                        <>
                          <Check size={12} aria-hidden />
                          {t("common:dashboard_grid.on_canvas")}
                        </>
                      ) : (
                        <>
                          <Plus size={12} aria-hidden />
                          {t("common:buttons.add")}
                        </>
                      )}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="shrink-0 border-t border-border px-4 py-2.5">
        <p className="text-widget-label text-muted-foreground">
          {remaining === 0
            ? t("common:dashboard_grid.every_widget_on_canvas")
            : t("common:dashboard_grid.remaining_of_total_available", {
                count: remaining,
                total: widgets.length,
              })}
        </p>
      </div>
    </Drawer>
  );
};
